import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../axios';
import { PaginatedResponse } from '../pagination/pagination.types';

export interface IBezugsperson {
    id: string;
    relationType: 'BEZUGSPERSON';
    salutation?: string | null;
    firstName: string;
    lastName: string;
    position: string;
    phoneNumber: string;
    email: string;
    isSelected: boolean;
}

export interface IBezugspersonPerson {
    id: string;
    firstName: string | null;
    lastName: string | null;
}

export interface IBezugspersonWithPerson {
    id: string;
    relationType: 'BEZUGSPERSON';
    salutation?: string | null;
    firstName: string | null;
    lastName: string | null;
    position: string | null;
    phoneNumber: string | null;
    email: string | null;
    isSelected: boolean;
    person: IBezugspersonPerson;
}

export interface IListAllBezugspersonenParams {
    page?: number;
    size?: number;
    search?: string;
}

export function listAllBezugspersonen(params: IListAllBezugspersonenParams = {}) {
    return request<PaginatedResponse<IBezugspersonWithPerson>>({
        url: '/api/persons/bezugspersonen',
        method: 'GET',
        params,
    });
}

export function getListAllBezugspersonenQueryKey(params: IListAllBezugspersonenParams = {}) {
    return ['/api/persons/bezugspersonen', params] as const;
}

export function useListAllBezugspersonenQuery(params: IListAllBezugspersonenParams = {}) {
    return useQuery({
        queryKey: getListAllBezugspersonenQueryKey(params),
        queryFn: () => listAllBezugspersonen(params),
        placeholderData: keepPreviousData,
    });
}

export interface ICreateBezugspersonPayload {
    firstName: string;
    lastName: string;
    position?: string;
    phoneNumber?: string;
    email?: string;
}

export function getBezugspersonen(personId: string) {
    return request<IBezugsperson[]>({
        url: `/api/persons/${personId}/bezugspersonen`,
        method: 'GET',
    });
}

export function getBezugspersonenQueryKey(personId: string) {
    return ['persons', personId, 'bezugspersonen'] as const;
}

export function useGetBezugspersonenQuery(personId: string, enabled = true) {
    return useQuery({
        queryKey: getBezugspersonenQueryKey(personId),
        queryFn: () => getBezugspersonen(personId),
        enabled: enabled && !!personId,
        placeholderData: keepPreviousData,
        staleTime: 5 * 60 * 1000,
    });
}

export function createBezugsperson(personId: string, data: ICreateBezugspersonPayload) {
    return request<IBezugsperson>({
        url: `/api/persons/${personId}/bezugspersonen`,
        method: 'POST',
        data,
    });
}

export function useCreateBezugspersonMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            personId,
            data,
        }: {
            personId: string;
            data: ICreateBezugspersonPayload;
        }) => createBezugsperson(personId, data),

        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: getBezugspersonenQueryKey(variables.personId),
            });
        },
    });
}

export type IUpdateBezugspersonPayload = IBezugsperson;

export function getBezugspersonById(personId: string, id: string) {
    return request<IBezugsperson>({
        url: `/api/persons/${personId}/bezugspersonen/${id}`,
        method: 'GET',
    });
}

export function getBezugspersonByIdQueryKey(personId: string, id: string) {
    return ['persons', personId, 'bezugspersonen', id] as const;
}

export function useGetBezugspersonByIdQuery(personId: string, id: string, enabled = true) {
    return useQuery({
        queryKey: getBezugspersonByIdQueryKey(personId, id),
        queryFn: () => getBezugspersonById(personId, id),
        enabled: enabled && !!personId && !!id,
    });
}

export function updateBezugsperson(personId: string, id: string, data: IUpdateBezugspersonPayload) {
    return request<IBezugsperson>({
        url: `/api/persons/${personId}/bezugspersonen/${id}`,
        method: 'PUT',
        data,
    });
}

export function useUpdateBezugspersonMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            personId,
            id,
            data,
        }: {
            personId: string;
            id: string;
            data: IUpdateBezugspersonPayload;
        }) => updateBezugsperson(personId, id, data),

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['/api/persons/bezugspersonen'],
            });
        },
    });
}